import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  Eye,
  FolderInput,
  Pencil,
  Plus,
  UserCheck,
} from "lucide-react";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { Chargement, Erreur } from "@/composants/ui/etats";
import { Donnee, Etiquette } from "@/composants/ui/etiquette";
import { LegendeEtapes, Timeline } from "@/composants/timeline";
import { Champ, ChampTexte } from "@/composants/ui/champ";
import { Documents, type DestinationPossible } from "@/composants/documents";
import { CloturerSurPreuve } from "@/composants/preuve-realisation";
import {
  FormulaireFinDuTransfert,
  FormulaireMaintienDuTransfert,
  FormulaireTransfert,
} from "@/composants/transfert";
import { FormulaireRegroupement } from "@/composants/regroupement";
import { Habilitations } from "@/composants/habilitations";
import { CommandesFournisseurs } from "@/composants/commandes-fournisseurs";
import { DemandesAvoirDuDossier } from "@/composants/demandes-avoir";
import {
  FormulaireLot,
  lotVide,
  useCategoriesCommande,
  type ValeursLot,
} from "@/composants/formulaire-lot";
import {
  AdresseEnBloc,
  ChampCategorieCommande,
  ChampsAdresse,
  ChampsNatures,
  depuisAdresse,
  enAdresse,
  libelleNatures,
  realisationRequise,
  type Natures,
} from "@/composants/champs-commande";
import { messageDe, useChargement } from "@/lib/chargement";
import {
  dateLongue,
  euros,
  nomPersonne,
  statutLot,
  statutCommande,
  statutReglement,
} from "@/lib/libelles";
import { api, type AchatsPrevisionnels, type FicheCommande } from "@/lib/api";
import { useSession } from "@/session";

/**
 * La vue synthétique du §4 : « timeline et prochaine action ».
 *
 * Elle répond à une seule question — que faire maintenant sur ce dossier ? Tout
 * ce qui n'y répond pas est en dessous : l'identité du dossier tient en haut,
 * les lots et leurs timelines suivent.
 *
 * L'écran ne parle de lot qu'à partir de deux (ADR-0031) : le lot unique d'une
 * commande non découpée n'y est qu'un « avancement ».
 */
export function PageCommande() {
  const { id = "" } = useParams();
  const session = useSession();
  const identite = session.statut === "connecte" ? session.identite : null;
  const aLaPermission = identite?.permissions.includes("GERER_COMMANDE_CLIENT") ?? false;
  // §17.2 : la clôture définitive appartient au Responsable commercial. Le
  // serveur le vérifie ; l'écran se contente de ne pas proposer un bouton qui
  // répondrait 403 — masquer n'est pas autoriser (`securite/profils.py`).
  const peutCloturer = identite?.permissions.includes("CLOTURER_DEFINITIVEMENT") ?? false;
  const voitLesMontants = identite?.permissions.includes("CONSULTER_MONTANTS") ?? false;
  const peutDeposer = identite?.permissions.includes("DEPOSER_DOCUMENT") ?? false;
  const commandeChezLesFournisseurs =
    identite?.permissions.includes("GERER_COMMANDE_FOURNISSEUR") ?? false;
  // Le terrain ne lit pas les fournisseurs (`profils.lit_les_fournisseurs`) : la
  // carte ne lui est pas montrée.
  const suitLesFournisseurs = identite?.lit_les_fournisseurs ?? false;

  const { donnees, erreur, enCours, recharger } = useChargement<FicheCommande>(
    (signal) => api.commande(id, signal),
    [id],
  );
  const [fiche, setFiche] = useState<FicheCommande | null>(null);
  const [ajout, setAjout] = useState(false);
  const [correction, setCorrection] = useState(false);
  const [transfert, setTransfert] = useState(false);
  const [finDuTransfert, setFinDuTransfert] = useState(false);
  const [maintien, setMaintien] = useState(false);
  const [regroupement, setRegroupement] = useState(false);
  const [action, setAction] = useState<string | null>(null);

  const courante = fiche ?? donnees;

  if (erreur) return <Erreur message={erreur} onReessayer={recharger} />;
  if (!courante || enCours) return <Chargement message="Chargement de la Commande / Projet…" />;

  const { commande, lots } = courante;
  // La permission dit le geste, `intervenable` dit la commande (ADR-0032) : un
  // habilité en consultation voit la fiche sans ses boutons.
  const peutGerer = aLaPermission && courante.intervenable;
  const decoupee = lots.length > 1;
  const statutsOffrables = courante.statuts_possibles.filter(
    (vise) => vise !== "CLOTUREE" || peutCloturer,
  );
  // Le BDC signé se rattache au dossier ; un plan ou une photo, à un lot.
  // Les deux sont proposés depuis le même formulaire.
  const destinations: DestinationPossible[] = [
    {
      valeur: "commande",
      libelle: `La Commande / Projet ${commande.numero_commande_client}`,
      cible: { commande_client_id: commande.id },
    },
    ...lots.map((lot) => ({
      valeur: lot.id,
      libelle: `${lot.code_lot} — ${lot.intitule}`,
      cible: { lot_id: lot.id },
    })),
  ];

  // Rend vrai si le serveur a accepté : un formulaire ne se ferme qu'alors.
  async function agir(travail: () => Promise<FicheCommande>) {
    setAction(null);
    try {
      setFiche(await travail());
      return true;
    } catch (cause) {
      setAction(messageDe(cause));
      return false;
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm text-encre-douce hover:text-encre"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Toutes les Commandes / Projets
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-mono text-xl font-semibold text-encre">
            {commande.numero_commande_client}
          </h1>
          <p className="mt-1 text-sm text-encre-douce">
            <Link
              to={`/clients/${commande.client.id}`}
              className="font-medium text-encre underline underline-offset-4"
            >
              {commande.client.designation}
            </Link>
            {commande.client.ville ? ` — ${commande.client.ville}` : ""}
          </p>
          {/* L'affaire regroupe les commandes d'un même dossier client (ADR-0028) ;
              la propriété est sur la commande, et c'est ici qu'on la transfère
              (ADR-0032). */}
          <p className="mt-1 text-sm text-encre-douce">
            Affaire{" "}
            <Link
              to={`/affaires/${commande.affaire.id}`}
              className="font-mono text-encre underline underline-offset-4"
            >
              {commande.affaire.numero}
            </Link>
            {" — "}
            propriétaire : {nomPersonne(commande.proprietaire)}
            {commande.vendeur_origine.id !== commande.proprietaire.id
              ? `, vendue par ${nomPersonne(commande.vendeur_origine)}`
              : ""}
          </p>
          {/* Réservé par le serveur à qui peut transférer (ADR-0043). */}
          {courante.transfert_temporaire ? (
            <p className="mt-1 text-sm text-encre-douce">
              Transférée jusqu'au {dateLongue(courante.transfert_temporaire.fin)}, revient à{" "}
              {nomPersonne(courante.transfert_temporaire.revient_a)}
            </p>
          ) : null}
          <div className="mt-2 flex flex-wrap gap-2">
            {courante.transferable && !transfert ? (
              <Bouton taille="compact" variante="discret" onClick={() => setTransfert(true)}>
                <UserCheck className="h-3.5 w-3.5" aria-hidden />
                Transférer
              </Bouton>
            ) : null}
            {courante.transfert_temporaire && !finDuTransfert ? (
              <Bouton
                taille="compact"
                variante="discret"
                onClick={() => {
                  setMaintien(false);
                  setFinDuTransfert(true);
                }}
              >
                <CalendarClock className="h-3.5 w-3.5" aria-hidden />
                Modifier la date de fin
              </Bouton>
            ) : null}
            {(courante.transfert_temporaire || courante.retour_impossible) && !maintien ? (
              <Bouton
                taille="compact"
                variante="discret"
                onClick={() => {
                  setFinDuTransfert(false);
                  setMaintien(true);
                }}
              >
                <UserCheck className="h-3.5 w-3.5" aria-hidden />
                Garder chez le chargé d'affaires actuel
              </Bouton>
            ) : null}
            {courante.regroupable && !regroupement ? (
              <Bouton taille="compact" variante="discret" onClick={() => setRegroupement(true)}>
                <FolderInput className="h-3.5 w-3.5" aria-hidden />
                Regrouper dans une autre affaire
              </Bouton>
            ) : null}
          </div>
          {courante.transfert_temporaire && finDuTransfert ? (
            <FormulaireFinDuTransfert
              fin={courante.transfert_temporaire.fin}
              onAnnuler={() => setFinDuTransfert(false)}
              onValider={async (fin, motif) => {
                if (await agir(() => api.modifierFinDuTransfert(commande.id, fin, motif))) {
                  setFinDuTransfert(false);
                }
              }}
            />
          ) : null}
          {(courante.transfert_temporaire || courante.retour_impossible) && maintien ? (
            <FormulaireMaintienDuTransfert
              onAnnuler={() => setMaintien(false)}
              onValider={async (motif) => {
                if (await agir(() => api.garderChezLeProprietaireActuel(commande.id, motif))) {
                  setMaintien(false);
                }
              }}
            />
          ) : null}
        </div>
        <Etiquette
          ton={
            commande.statut === "ANNULEE"
              ? "alerte"
              : commande.statut === "CLOTUREE"
                ? "succes"
                : commande.statut === "EN_COURS"
                  ? "actif"
                  : "neutre"
          }
        >
          {statutCommande(commande.statut)}
        </Etiquette>
      </div>

      {action ? <Erreur message={action} /> : null}

      {courante.sans_intervention === null ? null : (
        <p
          role="status"
          className="flex items-start gap-2 rounded-[6px] border border-filet bg-chene-clair/25 px-4 py-3 text-sm text-encre-douce"
        >
          <Eye className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{courante.sans_intervention}</span>
        </p>
      )}

      {/* Réservé par le serveur à qui peut transférer (ADR-0043, décision 15). */}
      {courante.retour_impossible ? (
        <p
          role="status"
          className="flex items-start gap-2 rounded-[6px] border border-alerte/30 bg-alerte/5 px-4 py-3 text-sm text-alerte"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>
            Transfert temporaire échu le {dateLongue(courante.retour_impossible.fin)}, retour à{" "}
            {nomPersonne(courante.retour_impossible.revient_a)} impossible : à transférer.
          </span>
        </p>
      ) : null}

      {regroupement ? (
        <Carte>
          <EnTeteCarte>
            <h2 className="text-sm font-semibold text-encre">Regrouper la commande</h2>
          </EnTeteCarte>
          <CorpsCarte>
            <FormulaireRegroupement
              clientId={commande.client.id}
              affaireActuelle={commande.affaire.id}
              onAnnuler={() => setRegroupement(false)}
              onValider={async (affaireId, motif) => {
                await agir(() => api.regrouper(commande.id, affaireId, motif));
                setRegroupement(false);
              }}
            />
          </CorpsCarte>
        </Carte>
      ) : null}

      {transfert ? (
        <Carte>
          <EnTeteCarte>
            <h2 className="text-sm font-semibold text-encre">Transférer la commande</h2>
          </EnTeteCarte>
          <CorpsCarte>
            <FormulaireTransfert
              proprietaireActuel={commande.proprietaire}
              onAnnuler={() => setTransfert(false)}
              onValider={async (proprietaireId, fin, motif) => {
                if (await agir(() => api.transferer(commande.id, proprietaireId, fin, motif))) {
                  setTransfert(false);
                }
              }}
            />
          </CorpsCarte>
        </Carte>
      ) : null}

      {courante.exigences_requises > 0 ? (
        <p
          role="status"
          className="flex items-start gap-2 rounded-[6px] border border-alerte/30 bg-alerte/5 px-4 py-3 text-sm text-alerte"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>
            {courante.exigences_requises} document
            {courante.exigences_requises > 1 ? "s" : ""} obligatoire
            {courante.exigences_requises > 1 ? "s" : ""} manque
            {courante.exigences_requises > 1 ? "nt" : ""} encore à cette Commande / Projet. Tant qu'il en reste,
            la clôture définitive est bloquée.
          </span>
        </p>
      ) : null}

      {courante.cloture_proposable && commande.statut !== "CLOTUREE" ? (
        <p
          role="status"
          className="flex items-start gap-2 rounded-[6px] border border-succes/30 bg-succes/5 px-4 py-3 text-sm text-succes"
        >
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>
            Tous les lots sont clos : la clôture définitive peut être proposée. Elle reste un
            geste du Responsable commercial, et elle n'est jamais appliquée d'elle-même.
          </span>
        </p>
      ) : null}

      <Carte>
        <EnTeteCarte className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-encre">La Commande / Projet</h2>
          {peutGerer && !commande.cloturee_definitivement ? (
            <Bouton taille="compact" variante="discret" onClick={() => setCorrection((o) => !o)}>
              <Pencil className="h-3.5 w-3.5" aria-hidden />
              {correction ? "Fermer" : "Corriger"}
            </Bouton>
          ) : null}
        </EnTeteCarte>
        <CorpsCarte>
          {correction ? (
            <FormulaireCommande
              commande={commande}
              onAnnuler={() => setCorrection(false)}
              onValider={async (champs) => {
                await agir(() => api.corrigerCommande(commande.id, champs));
                setCorrection(false);
              }}
            />
          ) : (
          <>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-4 sm:grid-cols-3 lg:grid-cols-4">
            <Donnee libelle="Catégorie">{commande.categorie_libelle}</Donnee>
            <Donnee libelle="Remise">{libelleNatures(commande)}</Donnee>
            <Donnee libelle="Adresse de réalisation">
              <AdresseEnBloc adresse={commande.adresse_realisation} />
            </Donnee>
            <Donnee libelle="Adresse de facturation">
              <AdresseEnBloc adresse={commande.adresse_facturation} />
            </Donnee>
            <Donnee libelle="Date de commande">{dateLongue(commande.date_commande)}</Donnee>
            <Donnee libelle="Date de signature">{dateLongue(commande.date_signature)}</Donnee>
            {voitLesMontants ? (
              <>
                <Donnee libelle="Montant HT">
                  <span className="tabular-nums">{euros(commande.montant_ht)}</span>
                </Donnee>
                <Donnee libelle="Encaissement">{statutReglement(commande.statut_reglement)}</Donnee>
                <Donnee libelle="TVA à 10 %">
                  {commande.tva_10_applicable
                    ? `Oui (${commande.tva_10_origine === "OCR" ? "détectée au BDC" : "déclarée"})`
                    : "Non"}
                </Donnee>
                {courante.achats_previsionnels ? (
                  <CoutPrevisionnel achats={courante.achats_previsionnels} />
                ) : null}
              </>
            ) : null}
          </dl>
          {commande.commentaires ? (
            <p className="mt-4 border-t border-filet pt-3 text-sm text-encre-douce">
              {commande.commentaires}
            </p>
          ) : null}
          </>
          )}
        </CorpsCarte>
      </Carte>

      {suitLesFournisseurs ? (
        <CommandesFournisseurs
          commandeClientId={commande.id}
          peutCommander={commandeChezLesFournisseurs && courante.intervenable}
          voitLesMontants={voitLesMontants}
        />
      ) : null}

      {suitLesFournisseurs ? <DemandesAvoirDuDossier demandes={courante.demandes_avoir} /> : null}

      <Documents
        // Un dépôt fait ailleurs sur la page (la preuve de réalisation) change
        // le compte : la liste se recharge.
        key={courante.documents}
        cible={{ commande_client_id: commande.id }}
        destinations={destinations}
        categorieProposee="BDC_SIGNE"
        ecriture={courante.intervenable}
      />

      {courante.intervenable || courante.habilitable ? (
        <Habilitations commandeId={commande.id} habilitable={courante.habilitable} />
      ) : null}

      {peutGerer && statutsOffrables.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-encre-douce">Faire passer cette Commande / Projet à :</span>
          {statutsOffrables.map((vise) => (
            <Bouton
              key={vise}
              taille="compact"
              onClick={() => void agir(() => api.changerStatutCommande(commande.id, vise))}
            >
              {statutCommande(vise)}
            </Bouton>
          ))}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-encre">
          {decoupee ? `${lots.length} lots` : "Avancement"}
        </h2>
        {peutGerer && !ajout && !commande.cloturee_definitivement ? (
          <Bouton taille="compact" onClick={() => setAjout(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            {decoupee ? "Ajouter un lot" : "Découper en lots"}
          </Bouton>
        ) : null}
      </div>

      {ajout ? (
        <Carte>
          <EnTeteCarte>
            <h3 className="text-sm font-semibold text-encre">Nouveau lot</h3>
            {decoupee ? null : (
              <p className="mt-1 text-sm text-encre-douce">
                Le suivi actuel devient le lot {lots[0]?.code_lot}, avec la catégorie de la
                commande ; elle se corrige ensuite sur sa fiche. Celui-ci s'y ajoute.
              </p>
            )}
          </EnTeteCarte>
          <CorpsCarte>
            <FormulaireLot
              initial={lotVide(commande.categorie)}
              onAnnuler={() => setAjout(false)}
              onValider={async (valeurs: ValeursLot) => {
                await agir(() => api.ajouterLot(commande.id, valeurs));
                setAjout(false);
              }}
            />
          </CorpsCarte>
        </Carte>
      ) : null}

      {lots.length > 0 ? <LegendeEtapes /> : null}

      <div className={decoupee ? "grid gap-4 md:grid-cols-2" : "grid gap-4"}>
        {lots.map((lot) => (
          <Carte key={lot.id}>
            <EnTeteCarte className="flex items-start justify-between gap-3">
              {decoupee ? (
                <div className="min-w-0">
                  <Link
                    to={`/lots/${lot.id}`}
                    className="font-mono text-sm text-encre-douce underline underline-offset-4"
                  >
                    {lot.code_lot}
                  </Link>
                  <p className="font-medium text-encre">{lot.intitule}</p>
                  <p className="text-sm text-encre-douce">{lot.categorie_libelle}</p>
                </div>
              ) : (
                <Link
                  to={`/lots/${lot.id}`}
                  className="font-mono text-sm text-encre-douce underline underline-offset-4"
                >
                  {lot.code_lot}
                </Link>
              )}
              <Etiquette>{statutLot(lot.statut_affiche)}</Etiquette>
            </EnTeteCarte>
            <CorpsCarte>
              <Timeline etapes={lot.etapes} prochaineAction={lot.prochaine_action} />
              {peutGerer &&
              peutDeposer &&
              lot.statut === "EN_COURS" &&
              !commande.cloturee_definitivement ? (
                <CloturerSurPreuve
                  lot={lot}
                  onClos={async () => {
                    await agir(() => api.commande(commande.id));
                  }}
                />
              ) : null}
            </CorpsCarte>
          </Carte>
        ))}
      </div>
    </div>
  );
}

/**
 * La correction d'un dossier — sans le numéro de commande.
 *
 * Il est la référence commerciale officielle (§3.1) et il engendre le code de
 * chaque lot, que le trigger rend immuable. Le champ n'est donc pas
 * proposé : offrir une case que le serveur refusera est pire que ne rien offrir.
 */
function FormulaireCommande({
  commande,
  onValider,
  onAnnuler,
}: {
  commande: FicheCommande["commande"];
  onValider: (champs: Record<string, unknown>) => Promise<void>;
  onAnnuler: () => void;
}) {
  const categories = useCategoriesCommande();
  const [categorie, setCategorie] = useState(commande.categorie);
  const [natures, setNatures] = useState<Natures>({
    mode_remise: commande.mode_remise,
    avec_pose: commande.avec_pose,
  });
  const [realisation, setRealisation] = useState(depuisAdresse(commande.adresse_realisation));
  const [facturation, setFacturation] = useState(depuisAdresse(commande.adresse_facturation));
  const [valeurs, setValeurs] = useState({
    date_commande: commande.date_commande,
    date_signature: commande.date_signature ?? "",
    montant_ht: commande.montant_ht === null ? "" : String(commande.montant_ht),
    // `null` signifie « champ fermé à ce profil ». Ce formulaire n'est ouvert
    // qu'à qui voit les montants, mais une case à cocher n'a que deux états :
    // le type doit rester honnête plutôt que s'appuyer sur cette coïncidence.
    tva_10_applicable: commande.tva_10_applicable ?? false,
    commentaires: commande.commentaires ?? "",
  });
  const [envoi, setEnvoi] = useState(false);

  const corps = (): Record<string, unknown> => ({
    date_commande: valeurs.date_commande,
    date_signature: valeurs.date_signature || null,
    montant_ht: valeurs.montant_ht === "" ? null : valeurs.montant_ht,
    tva_10_applicable: valeurs.tva_10_applicable,
    commentaires: valeurs.commentaires || null,
    // Les natures sont figées dès qu'un lot est clos : c'est la base qui le
    // dit, et son message remonte tel quel (ADR-0031).
    categorie,
    ...natures,
    adresse_realisation: enAdresse(realisation),
    adresse_facturation: enAdresse(facturation),
  });
  // Ce que le formulaire montrait à l'ouverture, lu une seule fois.
  const [depart] = useState(corps);

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        try {
          // Seul ce qui a changé part : corriger l'adresse ne doit pas renvoyer
          // les natures, que la base refuse de toucher dès qu'un lot est clos.
          const modifies = Object.fromEntries(
            Object.entries(corps()).filter(
              ([champ, valeur]) => JSON.stringify(valeur) !== JSON.stringify(depart[champ]),
            ),
          );
          if (Object.keys(modifies).length === 0) onAnnuler();
          else await onValider(modifies);
        } finally {
          setEnvoi(false);
        }
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Champ
          libelle="Date de commande"
          type="date"
          required
          value={valeurs.date_commande}
          onChange={(e) => setValeurs({ ...valeurs, date_commande: e.target.value })}
        />
        <Champ
          libelle="Date de signature"
          type="date"
          value={valeurs.date_signature}
          onChange={(e) => setValeurs({ ...valeurs, date_signature: e.target.value })}
        />
        <Champ
          libelle="Montant HT"
          type="number"
          min="0"
          step="0.01"
          value={valeurs.montant_ht}
          onChange={(e) => setValeurs({ ...valeurs, montant_ht: e.target.value })}
        />
        <label className="flex items-center gap-2 self-end pb-3 text-sm text-encre">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={valeurs.tva_10_applicable}
            disabled={commande.tva_10_origine === "OCR"}
            onChange={(e) => setValeurs({ ...valeurs, tva_10_applicable: e.target.checked })}
          />
          TVA à 10 % applicable
        </label>
        <ChampCategorieCommande
          categories={categories.options}
          valeur={categorie}
          onChanger={setCategorie}
        />
        <ChampsNatures valeurs={natures} onChanger={setNatures} />
        <ChampTexte
          libelle="Commentaires"
          className="sm:col-span-2"
          value={valeurs.commentaires}
          onChange={(e) => setValeurs({ ...valeurs, commentaires: e.target.value })}
        />
      </div>
      <h3 className="text-sm font-semibold text-encre">Adresse de réalisation</h3>
      <ChampsAdresse
        prefixe="realisation"
        valeurs={realisation}
        onChanger={setRealisation}
        requise={realisationRequise(natures)}
      />
      <h3 className="text-sm font-semibold text-encre">Adresse de facturation</h3>
      <ChampsAdresse prefixe="facturation" valeurs={facturation} onChanger={setFacturation} requise />
      {commande.tva_10_origine === "OCR" ? (
        <p className="text-xs text-encre-douce">
          La TVA à 10 % a été détectée dans le BDC : elle ne se retire pas ici. Il faut lever
          l'exigence documentaire avec un motif (ADR-0017).
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" variante="principal" disabled={envoi}>
          {envoi ? "Enregistrement…" : "Enregistrer"}
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler} disabled={envoi}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}

/**
 * « Coût prévisionnel incomplet : 3 lignes sans prix, frais de 2 commandes non répartis »,
 * ou rien. Les frais en attente de décision qu'un prix manquant empêche de répartir en
 * font partie.
 */
function phraseIncompletude(achats: AchatsPrevisionnels): string | null {
  const manques: string[] = [];
  if (achats.lignes_sans_prix > 0) {
    manques.push(
      `${achats.lignes_sans_prix} ${achats.lignes_sans_prix > 1 ? "lignes" : "ligne"} sans prix`,
    );
  }
  if (achats.commandes_frais_non_repartis > 0) {
    manques.push(
      `frais de ${achats.commandes_frais_non_repartis} ${
        achats.commandes_frais_non_repartis > 1 ? "commandes" : "commande"
      } non répartis`,
    );
  }
  if (achats.frais_en_attente_non_repartis > 0) {
    manques.push(
      `${achats.frais_en_attente_non_repartis} frais en attente ${
        achats.frais_en_attente_non_repartis > 1 ? "non répartis" : "non réparti"
      }`,
    );
  }
  return manques.length > 0 ? `Coût prévisionnel incomplet : ${manques.join(", ")}` : null;
}

/**
 * Ce que les achats de la commande doivent coûter d'après ses commandes
 * fournisseurs, et ce qu'il reste du prix de vente. Ni pose, ni transport, ni
 * fabrication : ce n'est pas la marge du dossier. Ticket #68 : le coût compte ce
 * qui est connu et dit en toutes lettres ce qu'il ne compte pas ; la marge en
 * garde la trace par « incomplète ».
 */
function CoutPrevisionnel({ achats }: { achats: AchatsPrevisionnels }) {
  const incompletude = phraseIncompletude(achats);
  return (
    <>
      <Donnee libelle="Coût d'achat prévisionnel">
        <span className="tabular-nums">
          {euros(achats.cout_achat_previsionnel_ht)}
          <span className="block text-xs text-encre-douce">
            dont {euros(achats.frais_ht)} de frais fournisseur
          </span>
          {incompletude === null ? null : (
            <span className="block text-xs text-encre">{incompletude}</span>
          )}
        </span>
      </Donnee>
      <Donnee libelle="Marge prévisionnelle sur achats">
        {achats.marge_previsionnelle_achats_ht === null ? (
          "Sans montant de vente"
        ) : (
          <span className="tabular-nums">
            {euros(achats.marge_previsionnelle_achats_ht)}
            {achats.incomplet ? <span className="text-xs text-encre"> incomplète</span> : null}
          </span>
        )}
      </Donnee>
      <Donnee libelle="Achats prévus">
        <span className="tabular-nums">
          {euros(achats.achats_prevus_ht)}
          <span className="block text-xs text-encre-douce">
            dont engagés : {euros(achats.achats_engages_ht)}
          </span>
        </span>
      </Donnee>
      {achats.frais_en_attente_ht > 0 ? (
        <Donnee libelle="Frais en attente de décision">
          <span className="tabular-nums">
            {euros(achats.frais_en_attente_ht)}
            <span className="block text-xs text-encre-douce">ils ne comptent pas encore</span>
          </span>
        </Donnee>
      ) : null}
    </>
  );
}
